import React, { useRef, useState } from 'react';

import { Tooltip, useMediaQuery, useTheme } from '@material-ui/core';
import CheckCircleOutlinedIcon from '@material-ui/icons/CheckCircleOutlined';
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined';
import PeopleOutlinedIcon from '@material-ui/icons/PeopleOutlined';
import PrintOutlinedIcon from '@material-ui/icons/PrintOutlined';
import ReplayIcon from '@material-ui/icons/Replay';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import { useReactToPrint } from 'react-to-print';

import PrintOptionsDialog, { PrintOptions } from 'components/_dialogs/PrintOptionsDialog/PrintOptionsDialog';
import ActionLibraryInstancePrintTemplate from 'components/_print/_templates/ActionLibraryInstancePrintTemplate/ActionLibraryInstancePrintTemplate';
import CenteredGrid from 'components/CenteredGrid/CenteredGrid';
import ColoredButton from 'components/ColoredButton';
import FormButtons from 'components/FormButtons';
import actionLibraryInstancesApi from 'config/api/actionLibraryInstance/actionLibraryInstance';
import type { ActionLibraryInstance } from 'config/api/actionLibraryInstance/actionLibraryInstance.types';
import ROLES from 'config/constants/ROLES';
import usePermissions from 'hooks/usePermissions';
import action_library_messages from 'messages/action_library_messages';
import { useConfirmationModalContext } from 'reactContext/ConfirmationModalContext/ConfirmationModalContext';

import DelegateInstanceDialog from '../DelegateInstanceDialog/DelegateInstanceDialog';

type Props = {
  canComplete: boolean;
  canReopen?: boolean;
  completeDisabledTooltip: string;
  instance: ActionLibraryInstance;
  onCompleteClick: () => void;
  onDelete: () => void;
  onInstanceUpdate?: () => void;
  onReopenClick?: () => void;
};

const ActionLibraryInstanceActions: React.FC<Props> = ({
  canComplete,
  canReopen,
  completeDisabledTooltip,
  instance,
  onCompleteClick,
  onDelete,
  onInstanceUpdate,
  onReopenClick,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { showConfirmationModal } = useConfirmationModalContext();
  const queryClient = useQueryClient();
  const [isOrgAssetAdmin] = usePermissions([ROLES.ORGANIZATION.ASSETS_MANAGER]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const [delegateDialogOpen, setDelegateDialogOpen] = useState(false);
  const [printDialogOpen, setPrintDialogOpen] = useState(false);
  const [printOptions, setPrintOptions] = useState<PrintOptions | undefined>(undefined);
  const [isPrinting, setIsPrinting] = useState(false);
  const printComponentRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    content: () => printComponentRef.current,
  });

  const handlePrintClick = () => {
    setPrintDialogOpen(true);
  };

  const handlePrintConfirm = (options: PrintOptions) => {
    setPrintDialogOpen(false);
    setPrintOptions(options);
    setIsPrinting(true);
  };

  React.useEffect(() => {
    if (!isPrinting || !printOptions) return undefined;
    const timer = setTimeout(() => {
      handlePrint();
      setIsPrinting(false);
    }, 150);
    return () => clearTimeout(timer);
  }, [isPrinting, printOptions, handlePrint]);

  const canDelete = instance.canDelete === true;

  const { mutate: deleteInstance, isLoading: deleteLoading } = useMutation(actionLibraryInstancesApi.deleteActionLibraryInstance, {
    onSuccess: () => {
      queryClient.invalidateQueries('ACTION_LIBRARY_INSTANCES_GET_ALL');
      enqueueSnackbar(t(action_library_messages.success.instance_deleted), { variant: 'success' });
      onDelete();
    },
    onError: () => {
      enqueueSnackbar(t(action_library_messages.errors.instance_create_failed), { variant: 'error' });
    },
  });

  const handleDelete = async () => {
    const confirmed = await showConfirmationModal({
      title: t(action_library_messages.delete_confirmation.title),
      body: t(action_library_messages.delete_confirmation.body),
    });

    if (confirmed) {
      deleteInstance(instance.id);
    }
  };

  const handleDelegateSuccess = () => {
    onInstanceUpdate?.();
  };

  return (
    <CenteredGrid width='lg' withoutPadding>
      <FormButtons inline={!isMobile}>
        {instance.status !== 'completed' &&
          (!canComplete ? (
            <Tooltip title={completeDisabledTooltip}>
              <span>
                <ColoredButton
                  className=''
                  customColor='secondary'
                  disabled
                  icon={false}
                  showLoader={false}
                  startIcon={<CheckCircleOutlinedIcon />}
                  textLike={false}
                  variant='outlined'
                >
                  {t(action_library_messages.instance_actions.complete_instance)}
                </ColoredButton>
              </span>
            </Tooltip>
          ) : (
            <ColoredButton
              className=''
              customColor='secondary'
              disabled={false}
              icon={false}
              onClick={onCompleteClick}
              showLoader={false}
              startIcon={<CheckCircleOutlinedIcon />}
              textLike={false}
              variant='outlined'
            >
              {t(action_library_messages.instance_actions.complete_instance)}
            </ColoredButton>
          ))}
        {instance.status === 'completed' && canReopen && onReopenClick && (
          <ColoredButton
            className=''
            customColor='secondary'
            disabled={false}
            fullWidth={false}
            icon={false}
            onClick={onReopenClick}
            showLoader={false}
            startIcon={<ReplayIcon />}
            textLike={false}
            variant='outlined'
          >
            {t(action_library_messages.instance_actions.reopen_instance)}
          </ColoredButton>
        )}

        <ColoredButton
          className=''
          customColor='secondary'
          disabled={false}
          icon={false}
          onClick={handlePrintClick}
          showLoader={false}
          startIcon={<PrintOutlinedIcon />}
          textLike={false}
          variant='outlined'
        >
          {t(action_library_messages.actions.print)}
        </ColoredButton>
        {/* Hidden print template - only mount with options so template never receives undefined */}
        <div style={{ display: 'none' }}>
          <div ref={printComponentRef}>
            {printOptions ? (
              <ActionLibraryInstancePrintTemplate key={JSON.stringify(printOptions)} instance={instance} options={printOptions} />
            ) : (
              <div />
            )}
          </div>
        </div>
        <PrintOptionsDialog onClose={() => setPrintDialogOpen(false)} onPrint={handlePrintConfirm} open={printDialogOpen} />

        {isOrgAssetAdmin && (
          <Tooltip title={instance.status === 'completed' ? t(action_library_messages.instance_actions.delegate_completed_blocked) : ''}>
            <span>
              <ColoredButton
                className=''
                customColor='secondary'
                disabled={instance.status === 'completed'}
                fullWidth
                icon={false}
                onClick={() => setDelegateDialogOpen(true)}
                showLoader={false}
                startIcon={<PeopleOutlinedIcon />}
                textLike={false}
                variant='outlined'
              >
                {t(action_library_messages.instance_actions.delegate)}
              </ColoredButton>
            </span>
          </Tooltip>
        )}
        {canDelete && (
          <Tooltip title={instance.status === 'completed' ? t(action_library_messages.instance_actions.delete_completed_blocked) : ''}>
            <span>
              <ColoredButton
                className=''
                customColor={instance.status === 'completed' ? 'secondary' : 'danger'}
                disabled={deleteLoading || instance.status === 'completed'}
                icon={false}
                onClick={handleDelete}
                showLoader={deleteLoading}
                startIcon={<DeleteOutlinedIcon />}
                textLike={false}
                variant='outlined'
              >
                {t(action_library_messages.actions.delete)}
              </ColoredButton>
            </span>
          </Tooltip>
        )}
      </FormButtons>

      <DelegateInstanceDialog
        instance={instance}
        onClose={() => setDelegateDialogOpen(false)}
        onSuccess={handleDelegateSuccess}
        open={delegateDialogOpen}
      />
    </CenteredGrid>
  );
};

export default ActionLibraryInstanceActions;
